/* Patch 20 — CFO Daily Email Brief — Preview + Settings + History
   ================================================================
   /cfo-email-preview · CFO can:
     • Preview the next-day brief composed live from current data.
     • Configure recipients + cc list and toggle the daily schedule.
     • Trigger a manual send (uses the threshold-based subject prefix
       and writes an audit row).
     • Browse past dispatches (status, alert flag, key counters). */
import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Send, Save, Mail, AlertTriangle, CheckCircle2,
  Clock, History, ExternalLink, FileText,
} from 'lucide-react';

const SEV_COLOR = {
  critical: 'text-rose-700  bg-rose-50  border-rose-200',
  high:     'text-amber-700 bg-amber-50 border-amber-200',
  medium:   'text-sky-700   bg-sky-50   border-sky-200',
  low:      'text-slate-600 bg-slate-50 border-slate-200',
};

export default function CfoEmailPreview() {
  const [brief, setBrief] = useState(null);
  const [settings, setSettings] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [enabled, setEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, h] = await Promise.all([
        axiosInstance.get('/cfo-brief/preview'),
        axiosInstance.get('/cfo-brief/settings'),
        axiosInstance.get('/cfo-brief/history?limit=20'),
      ]);
      setBrief(p.data);
      setSettings(s.data);
      setHistoryRows(h.data || []);
      setEnabled(!!s.data?.enabled);
      setRecipientsInput((s.data?.recipients || []).join(', '));
      setCcInput((s.data?.cc || []).join(', '));
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const parseList = (raw) =>
    (raw || '')
      .split(/[,\n;]+/)
      .map(s => s.trim())
      .filter(Boolean);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put('/cfo-brief/settings', {
        enabled,
        recipients: parseList(recipientsInput),
        cc:         parseList(ccInput),
      });
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    const recipients = parseList(recipientsInput);
    if (recipients.length === 0) {
      toast.error('Add at least one recipient before sending.');
      return;
    }
    setSending(true);
    try {
      const res = await axiosInstance.post('/cfo-brief/send', {
        recipients,
        cc: parseList(ccInput),
      });
      toast.success(`Sent · ${res.data.subject}`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Send failed: ${typeof d === 'object' ? (d.error || JSON.stringify(d)) : (d || e.message)}`);
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto" data-testid="cfo-brief-loading">
        <div className="inline-flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="animate-spin" size={14} /> Loading CFO brief…
        </div>
      </div>
    );
  }

  if (!brief) return null;
  const q = brief.action_queue || {};
  const queues = q.queues || [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="cfo-brief-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Mail className="text-indigo-600" size={26} /> CFO Daily Brief
          </h1>
          <p className="text-sm text-slate-600">
            Live preview of the executive email — composed from the same
            data the CFO Dashboard renders. Sends daily at 06:00
            Asia/Riyadh when enabled.
          </p>
        </div>
        <button
          data-testid="cfo-brief-refresh-btn"
          onClick={load}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold"
        >
          <RefreshCw size={14} /> Refresh
        </button>
        <button
          data-testid="cfo-brief-board-pack-btn"
          onClick={() => {
            axiosInstance
              .get('/cfo-brief/board-pack-pdf', { responseType: 'blob' })
              .then((r) => {
                const url = URL.createObjectURL(r.data);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              })
              .catch(() => toast.error('Board Pack PDF generation failed'));
          }}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded px-4 py-1.5 text-sm font-semibold ml-2"
        >
          <FileText size={14} /> Board Pack PDF
        </button>
      </header>

      {/* Settings & manual dispatch */}
      <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="cfo-brief-settings">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
          Recipients & Schedule
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <label className="block">
            <div className="text-xs font-semibold text-slate-700 mb-1">To (comma-separated)</div>
            <input
              data-testid="cfo-brief-recipients-input"
              value={recipientsInput}
              onChange={e => setRecipientsInput(e.target.value)}
              placeholder="cfo@example.com, controller@example.com"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
            />
          </label>
          <label className="block">
            <div className="text-xs font-semibold text-slate-700 mb-1">CC (optional)</div>
            <input
              data-testid="cfo-brief-cc-input"
              value={ccInput}
              onChange={e => setCcInput(e.target.value)}
              placeholder="audit@example.com"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
            />
          </label>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="cfo-brief-enabled-toggle"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              className="w-4 h-4"
            />
            Send daily at 06:00 Asia/Riyadh
          </label>
          <div className="flex items-center gap-2">
            <button
              data-testid="cfo-brief-save-btn"
              onClick={saveSettings}
              disabled={saving}
              className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save Settings
            </button>
            <button
              data-testid="cfo-brief-send-btn"
              onClick={sendNow}
              disabled={sending}
              className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Send Now
            </button>
          </div>
        </div>
      </section>

      {/* Brief preview */}
      <section
        className={`rounded-lg border-2 ${brief.is_alert ? 'border-rose-300 bg-rose-50' : 'border-emerald-300 bg-emerald-50'}`}
        data-testid="cfo-brief-preview-card"
      >
        <div className="px-5 py-3 border-b border-current/10 flex items-center justify-between">
          <div className="text-sm font-bold inline-flex items-center gap-2">
            {brief.is_alert
              ? <><AlertTriangle size={14} /> ⚠ IMMEDIATE ATTENTION REQUIRED</>
              : <><CheckCircle2 size={14} /> ✓ All systems within thresholds</>}
          </div>
          <div className="text-xs font-mono text-slate-600">{brief.date}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          <div className="bg-white rounded p-3 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500">Close Confidence</div>
            <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="cfo-brief-confidence">
              {brief.close_confidence.score}<span className="text-base text-slate-400">/100</span>
            </div>
            <div className="text-xs font-bold text-slate-600 mt-0.5">{brief.close_confidence.label}</div>
          </div>
          <div className="bg-white rounded p-3 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500">Financial Integrity</div>
            <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="cfo-brief-integrity">
              {brief.integrity_score != null ? brief.integrity_score : '—'}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">{brief.reconciliation?.verdict || '—'}</div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg" data-testid="cfo-brief-queue-card">
        <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Action Queue ({q.total || 0} pending)
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {queues.map(qq => (
              <tr key={qq.key} data-testid={`cfo-brief-queue-${qq.key}`} className="hover:bg-slate-50">
                <td className="px-4 py-2 text-slate-700">{qq.label}</td>
                <td className="px-4 py-2 text-right">
                  <span className={`px-2 py-0.5 rounded border text-xs font-mono font-bold ${SEV_COLOR[qq.severity] || ''}`}>
                    {qq.count}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <a href={qq.link} className="text-indigo-600 hover:text-indigo-800 text-xs inline-flex items-center gap-1">
                    Open <ExternalLink size={10} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="cfo-brief-recon-card">
          <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Reconciliation Snapshot
          </div>
          <ul className="p-4 space-y-1 text-sm">
            {(brief.reconciliation?.checks || []).map((c, i) => (
              <li key={i} className="flex justify-between" data-testid={`cfo-brief-recon-row-${i}`}>
                <span className="text-slate-700">{c.name}</span>
                <span className={`font-bold text-xs ${
                  c.status === 'FAIL' ? 'text-rose-700' :
                  c.status === 'WARNING' ? 'text-amber-700' : 'text-emerald-700'
                }`}>{c.status}</span>
              </li>
            ))}
            {(brief.reconciliation?.checks || []).length === 0 && (
              <li className="text-slate-400 text-xs">No checks available</li>
            )}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="cfo-brief-risks-card">
          <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Top Risks
          </div>
          <ul className="p-4 space-y-1 text-sm">
            {(brief.top_risks || []).map((r, i) => (
              <li key={i} className="text-slate-700" data-testid={`cfo-brief-risk-${i}`}>
                <AlertTriangle className="inline mr-1 text-rose-600" size={12} /> {r}
              </li>
            ))}
            {(brief.top_risks || []).length === 0 && (
              <li className="text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 size={12} /> No top risks
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* History */}
      <section className="bg-white border border-slate-200 rounded-lg" data-testid="cfo-brief-history-card">
        <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-2">
          <History size={11} /> Dispatch History
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Triggered</th>
              <th className="px-3 py-2 text-right">Confidence</th>
              <th className="px-3 py-2 text-right">Critical</th>
              <th className="px-3 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {historyRows.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-6 text-slate-500" data-testid="cfo-brief-history-empty">
                No dispatches yet. Use Send Now to generate the first one.
              </td></tr>
            ) : historyRows.map((r, i) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`cfo-brief-history-row-${i}`}>
                <td className="px-3 py-2 font-mono text-slate-500">
                  <Clock className="inline mr-1" size={10} />
                  {(r.generated_at || '').slice(0, 16).replace('T', ' ')}
                </td>
                <td className="px-3 py-2">
                  {r.is_alert && <span className="text-rose-700 font-bold">[ALERT] </span>}
                  {r.subject?.replace(/^\[ALERT\] /, '')}
                </td>
                <td className="px-3 py-2 text-slate-600">{r.triggered_by}</td>
                <td className="px-3 py-2 text-right font-mono">{r.confidence_score}</td>
                <td className="px-3 py-2 text-right font-mono">{r.critical_count}</td>
                <td className="px-3 py-2 text-right">
                  <span className={`text-[10px] font-bold ${
                    r.email_status === 'sent' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {(r.email_status || '').toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
